// ./src/components/features/Forms/IpFilterEditor/IpFilterEditor.tsx
import React, { useCallback } from "react";
import {
  Stack,
  TagsInput,
  SegmentedControl,
  TextInput,
  Text,
  Box,
  Tooltip,
  Badge,
  Group,
  InputLabel,
} from "@mantine/core";
import { AppIcon } from "@/components/generic";

export interface IpFilterValue {
  mode: "allow" | "deny";
  addresses: string[];
  message: string;
}

export interface IpFilterEditorProps {
  /** Current filter value */
  value: IpFilterValue;
  /** Callback when value changes */
  onChange: (value: IpFilterValue) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Optional description text */
  description?: string;
  premiumFeatureEnabled?: boolean;
}

export const IpFilterEditor: React.FC<IpFilterEditorProps> = ({
  value,
  onChange,
  disabled = false,
  description,
  premiumFeatureEnabled = false,
}) => {
  const handleModeChange = (mode: string) => {
    onChange({
      ...value,
      mode: mode as "allow" | "deny",
    });
  };

  const handleAddressesChange = (addresses: string[]) => {
    onChange({
      ...value,
      addresses,
    });
  };

  const handleMessageChange = (message: string) => {
    onChange({
      ...value,
      message,
    });
  };

  const premiumBadge = useCallback(
    (label?: string | null | undefined) => {
      return (
        !premiumFeatureEnabled && (
          <Group gap={6}>
            <InputLabel size="md">{label}</InputLabel>
            <Tooltip label={"Upgrade your plan to unlock this feature"}>
              <Badge
                size="xs"
                variant="light"
                color="yellow"
                leftSection={<AppIcon iconName="Lock" size={10} />}
              >
                Premium
              </Badge>
            </Tooltip>
          </Group>
        )
      );
    },
    [premiumFeatureEnabled]
  );

  return (
    <Stack gap="sm">
      {description && (
        <Text size="sm" c="dimmed">
          {description}
        </Text>
      )}

      <Box>
        <Text size="sm" fw={500} mb={4}>
          {premiumBadge("Filter Mode")}
        </Text>
        <SegmentedControl
          value={value.mode}
          onChange={handleModeChange}
          disabled={disabled}
          data={[
            { label: "Deny Listed IPs", value: "deny" },
            { label: "Allow Only Listed IPs", value: "allow" },
          ]}
          size="sm"
          fullWidth
        />
        <Text size="xs" c="dimmed" mt={4}>
          {value.mode === "deny"
            ? "Block submissions from the listed IP addresses"
            : "Only accept submissions from the listed IP addresses"}
        </Text>
      </Box>

      <TagsInput
        label={premiumBadge("IP Addresses")}
        placeholder="Enter IP address and press Enter"
        value={value.addresses}
        onChange={handleAddressesChange}
        disabled={!premiumFeatureEnabled || disabled}
        splitChars={[",", " ", ";"]}
        acceptValueOnBlur
        clearable
        description="Add IPv4 or IPv6 addresses. Press Enter after each address."
        size="md"
      />

      <TextInput
        label={premiumBadge("Custom Block Message")}
        placeholder="Submissions from your network are not permitted"
        value={value.message}
        onChange={(e) => handleMessageChange(e.target.value)}
        disabled={!premiumFeatureEnabled || disabled}
        description="Message shown to users when their IP is blocked (optional)"
        size="md"
      />
    </Stack>
  );
};
